import React, { useState } from 'react';

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function CustomerFacingSubtotal({
  subtotal,
  processingFee = 0,
  formatCurrency,
  itemCount,
  dark = false,
  compact = false,
  rowStyle = {},
  labelStyle = {},
  valueStyle = {},
  processingFeeLabel,
  feeBreakdown,
  subLabel,
  dividerColor
}) {
  const [open, setOpen] = useState(false);
  const itemsSubtotal = numberOrZero(subtotal);
  const fee = Math.max(0, numberOrZero(processingFee));
  const hasProcessingFee = fee > 0;
  const visibleFeeBreakdown = Array.isArray(feeBreakdown)
    ? feeBreakdown.filter(row => numberOrZero(row.amount) > 0)
    : [];
  const displaySubtotal = itemsSubtotal + fee;
  const format = typeof formatCurrency === 'function'
    ? formatCurrency
    : (amount) => amount?.toLocaleString?.() || String(amount || 0);

  const textColor = dark ? '#f9fafb' : '#111827';
  const mutedColor = dark ? '#d1d5db' : '#6b7280';
  const borderColor = dividerColor || (dark ? 'rgba(255,255,255,0.12)' : '#f3f4f6');
  const sheetBg = dark ? '#111827' : '#ffffff';
  const sheetSurface = dark ? '#1f2937' : '#f9fafb';
  const sheetBorder = dark ? '#374151' : '#e5e7eb';
  const sheetMuted = dark ? '#9ca3af' : '#6b7280';

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: compact ? 'center' : 'flex-start',
          gap: '12px',
          padding: compact ? '0' : '12px 0',
          borderBottom: compact ? 'none' : `1px solid ${borderColor}`,
          ...rowStyle
        }}
      >
        <div style={{ minWidth: 0 }}>
          <button
            type="button"
            onClick={() => hasProcessingFee && setOpen(true)}
            disabled={!hasProcessingFee}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: 0,
              margin: 0,
              border: 0,
              background: 'transparent',
              color: hasProcessingFee ? textColor : mutedColor,
              font: 'inherit',
              fontSize: compact ? '14px' : '14px',
              fontWeight: compact ? 500 : 600,
              textAlign: 'left',
              cursor: hasProcessingFee ? 'pointer' : 'default',
              ...labelStyle
            }}
            aria-label={hasProcessingFee ? 'View subtotal breakdown' : undefined}
          >
            <span>{hasProcessingFee ? (processingFeeLabel || (compact ? 'Subtotal incl. transaction fee' : 'Subtotal incl. transaction fee')) : 'Subtotal'}</span>
            {hasProcessingFee && (
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: dark ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                  color: dark ? '#f9fafb' : '#6b7280',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                  flex: '0 0 auto'
                }}
              >
                i
              </span>
            )}
          </button>
          {subLabel !== false && (
            <p
              style={{
                color: mutedColor,
                fontSize: '12px',
                margin: compact ? '2px 0 0 0' : '3px 0 0 0'
              }}
            >
              {subLabel || (typeof itemCount === 'number' ? `${itemCount} item${itemCount === 1 ? '' : 's'}` : '')}
            </p>
          )}
        </div>
        <span
          style={{
            color: textColor,
            fontSize: compact ? '14px' : '16px',
            fontWeight: compact ? 600 : 700,
            whiteSpace: 'nowrap',
            ...valueStyle
          }}
        >
          {format(displaySubtotal)}
        </span>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Subtotal breakdown"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            backgroundColor: 'rgba(15, 23, 42, 0.42)',
            padding: '16px'
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '420px',
              backgroundColor: sheetBg,
              borderRadius: '18px 18px 14px 14px',
              boxShadow: dark ? '0 24px 70px rgba(0, 0, 0, 0.55)' : '0 24px 70px rgba(15, 23, 42, 0.24)',
              padding: '18px',
              color: textColor
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', lineHeight: 1.3, fontWeight: 800 }}>Subtotal breakdown</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: sheetMuted }}>
                  {visibleFeeBreakdown.length > 0
                    ? 'Fees are included in the subtotal shown at checkout.'
                    : 'Transaction fee is included in the subtotal shown at checkout.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '999px',
                  border: `1px solid ${sheetBorder}`,
                  backgroundColor: sheetSurface,
                  color: textColor,
                  fontSize: '18px',
                  lineHeight: 1,
                  cursor: 'pointer'
                }}
              >
                x
              </button>
            </div>

            <div style={{ display: 'grid', gap: '10px', fontSize: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: sheetMuted }}>Items</span>
                <strong style={{ color: textColor, fontWeight: 700 }}>{format(itemsSubtotal)}</strong>
              </div>
              {visibleFeeBreakdown.length > 0 ? (
                visibleFeeBreakdown.map((row) => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: sheetMuted }}>{row.label}</span>
                    <strong style={{ color: textColor, fontWeight: 700 }}>{format(numberOrZero(row.amount))}</strong>
                  </div>
                ))
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                  <span style={{ color: sheetMuted }}>Transaction fee</span>
                  <strong style={{ color: textColor, fontWeight: 700 }}>{format(fee)}</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', paddingTop: '12px', marginTop: '2px', borderTop: `1px solid ${sheetBorder}` }}>
                <span style={{ color: textColor, fontWeight: 800 }}>Subtotal</span>
                <strong style={{ color: textColor, fontWeight: 800 }}>{format(displaySubtotal)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
